import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal, setCurrentStep, loadOnboardingStatus } from "../../redux/slices/OfficeOnboardingSlice";
import { AppDispatch, RootState } from "../../redux/store";
import WelcomeStep from "../../screens/SignupSteps/WelcomeStep";
import Step1 from "../../screens/SignupSteps/Step1";
import Step2 from "../../screens/SignupSteps/Step2";
import Step3 from "../../screens/SignupSteps/Step3";
import Step4 from "../../screens/SignupSteps/Step4";
import Step5 from "../../screens/SignupSteps/Step5";
import { logger } from "../../utils/logger";

const log = logger.getLogger("OnboardingModal");

const OnboardingModal: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isModalOpen, currentStep, totalSteps, isLoading, isCompleted } = useSelector((state: RootState) => state.officeOnboarding);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);
  const welcomeModeRef = useRef(false); // Track if we're in welcome mode
  const hasLoadedRef = useRef(false); // Track if we've already loaded the initial status

  useEffect(() => {
    if (isModalOpen) {
      // Only load status when modal first opens, not on step changes
      if (!hasLoadedRef.current) {
        hasLoadedRef.current = true;

        // ALWAYS check flag FIRST - if it exists, we're showing WelcomeStep, don't load from backend
        const showWelcomeModal = localStorage.getItem("showWelcomeModal");

        if (showWelcomeModal === "true") {
          // Flag exists - delete it, ensure step is 0, and DON'T load from backend
          localStorage.removeItem("showWelcomeModal");
          welcomeModeRef.current = true; // Mark as welcome mode
          dispatch(setCurrentStep(0));
          return;
        }

        // If we're already on step 0, don't reload (might override it)
        if (currentStep === 0) {
          welcomeModeRef.current = true; // Mark as welcome mode
          return; // Don't call loadOnboardingStatus
        }

        // Reset welcome mode flag if we're on a different step
        if (currentStep !== 0) {
          welcomeModeRef.current = false;
        }

        // Only load if NOT in welcome mode
        if (!welcomeModeRef.current) {
          const officeId = currentUser?.offices?.[0]?.officeId;
          dispatch(loadOnboardingStatus(officeId));
        }
      }
    } else {
      // Reset flags when modal closes
      welcomeModeRef.current = false;
      hasLoadedRef.current = false;
    }
  }, [isModalOpen, dispatch, currentUser]);

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleVideoPlay = () => {
    // Handle video play action
    log.debug("Video play clicked");
  };

  if (!isModalOpen) {
    return null;
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeStep />;
      case 1:
        return <Step1 />;
      case 2:
        return <Step2 />;
      case 3:
        return <Step3 />;
      case 4:
        return <Step4 />;
      case 5:
        return <Step5 />;
      default:
        return <WelcomeStep />;
    }
  };

  const progressPercentage = (currentStep / totalSteps) * 100;

  // Prevent closing on welcome step (step 0)
  const handleBackdropClick = () => {
    if (currentStep !== 0) {
      handleClose();
    }
  };

  // Use smaller container for welcome step (step 0)
  const isWelcomeStep = currentStep === 0;
  const modalContainerClass = isWelcomeStep
    ? "relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
    : "relative bg-white rounded-2xl shadow-2xl max-w-6xl w-full h-[80vh] flex flex-col overflow-hidden";

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={handleBackdropClick}>
        {/* Modal Container */}
        <div className={modalContainerClass} onClick={e => e.stopPropagation()}>
          {/* Step Content */}
          {isWelcomeStep ? (
            <div className="overflow-y-auto max-h-[90vh]">
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-docnote-orange"></div>
                </div>
              ) : (
                renderStep()
              )}
            </div>
          ) : (
            <div className="flex-1 min-h-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-20 h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-docnote-orange"></div>
                </div>
              ) : (
                renderStep()
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OnboardingModal;
